import { useFragment } from '@apollo/client/react';
import { compare } from 'compare-versions';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { auth, AuthError } from '@atlaskit/outbound-auth-flow-client';
import type { SourceType } from '@trello/analytics-types';
import { Analytics, formatContainers } from '@trello/atlassian-analytics';
import { getMemberIdWithFallback } from '@trello/authentication';
import {
  desktopVersion,
  identityBaseUrl,
  plannerOauthContainerId,
} from '@trello/config';
import {
  sendErrorEvent,
  type SentryErrorMetadata,
} from '@trello/error-reporting';
import { useFeatureGate } from '@trello/feature-gate-client';
import { showFlag } from '@trello/nachos/experimental-flags';
import { dangerouslyConvertPrivacyString } from '@trello/privacy';

import { PlannerMemberEmailFragmentDoc } from './PlannerHeaderToolbar/CalendarsPopover/PlannerMemberEmailFragment.generated';
import { GOOGLE_SERVICE_KEY } from './Planner.constants';
import { usePlannerAccounts } from './usePlannerAccounts';
import { useWorkspaceAri } from './useWorkspaceAri';

type UseAuthenticateCalendarArgs = {
  setIsAuthenticating?: (loading: boolean) => void;
  onSuccessfulAuth?: () => Promise<void>;
  analyticsSource?: SourceType;
};

/**
 * Returns a callback to authenticate a calendar account
 *
 * @param setIsAuthenticating - Callback to set the authenticating state
 * @param onSuccessfulAuth - Callback to handle successful authentication
 * @returns A callback to authenticate a calendar account
 */
export const useAuthenticateCalendar = ({
  setIsAuthenticating,
  onSuccessfulAuth,
  analyticsSource,
}: UseAuthenticateCalendarArgs) => {
  const intl = useIntl();
  const { workspaceId } = useWorkspaceAri();
  const { allAccounts } = usePlannerAccounts();
  const numberOfAccounts = allAccounts.length;

  const { value: isUseLoginHintEnabled } = useFeatureGate(
    'trello_electric_use_login_hint',
  );
  const memberId = getMemberIdWithFallback();

  const { data, complete } = useFragment({
    fragment: PlannerMemberEmailFragmentDoc,
    fragmentName: 'PlannerMemberEmail',
    from: {
      __typename: 'Member',
      id: memberId,
    },
    variables: { memberId },
  });

  // `complete` is checked here to mimic the current behavior. It narrows the type
  // of `email` to `Maybe<PIIString> | undefined`, which is later expected
  const memberData = complete ? data : undefined;
  const email = memberData?.email;

  const containers = formatContainers({
    workspaceId,
  });

  return useCallback(
    async (serviceKey: string) => {
      const analyticsSubject =
        serviceKey === GOOGLE_SERVICE_KEY
          ? 'googleCalendar'
          : 'outlookCalendar';

      Analytics.sendUIEvent({
        action: 'clicked',
        actionSubject: 'button',
        actionSubjectId:
          serviceKey === GOOGLE_SERVICE_KEY
            ? 'googleCalendarButton'
            : 'outlookCalendarButton',

        source: 'connectPlannerAccountInlineDialog',
        containers,

        attributes: {
          personalProductivity: 'planner',
          containerType: 'planner',
        },
      });

      setIsAuthenticating?.(true);

      const taskName = 'connect-plannerAccount';
      const source = analyticsSource ?? 'plannerScreen';
      const traceId = Analytics.startTask({
        taskName,
        source,
      });

      try {
        const OUTBOUND_AUTH_URL = `${identityBaseUrl}/outboundAuth/start?containerId=${plannerOauthContainerId}&serviceKey=${serviceKey}`;
        const params = new URLSearchParams({
          ...(email && { login_hint: dangerouslyConvertPrivacyString(email) }),
          continue: OUTBOUND_AUTH_URL,
        });
        const urlWithRedirects = `${identityBaseUrl}/login?${params.toString()}`;

        // urlWithRedirects will only work properly in desktop version 2.15.11 or above
        // in local development, desktopVersion is 0
        const useRedirectsInDesktop =
          desktopVersion === '0'
            ? true
            : compare(desktopVersion, '2.15.11', '>=');

        const authUrl =
          isUseLoginHintEnabled && useRedirectsInDesktop
            ? urlWithRedirects
            : OUTBOUND_AUTH_URL;
        await auth(authUrl);
        await onSuccessfulAuth?.();
        setIsAuthenticating?.(false);
        Analytics.sendTrackEvent({
          action: 'connected',
          actionSubject: 'plannerAccount',
          actionSubjectId: analyticsSubject,
          source,
          attributes: {
            personalProductivity: 'planner',
            containerType: 'planner',
            numAccounts: numberOfAccounts + 1,
          },
          containers,
        });
        Analytics.taskSucceeded({
          taskName,
          source,
          traceId,
        });
      } catch (error) {
        Analytics.sendTrackEvent({
          action: 'failed',
          actionSubject: 'plannerAccount',
          actionSubjectId: analyticsSubject,
          source,
          containers,
          attributes: {
            personalProductivity: 'planner',
            containerType: 'planner',
            numAccounts: numberOfAccounts,
          },
        });
        showFlag({
          id: 'connectPlannerAccount',
          title: intl.formatMessage({
            id: 'templates.planner.connect-error',
            defaultMessage:
              'There was an error connecting your account, please try again.',
            description:
              'Displays an error message when connecting an account fails',
          }),
          isAutoDismiss: false,
          appearance: 'error',
        });
        setIsAuthenticating?.(false);
        if (
          error instanceof AuthError &&
          (error.type === 'auth_window_closed' ||
            error.type === 'access_denied')
        ) {
          Analytics.taskAborted({
            taskName,
            source,
            traceId,
            error,
          });
          return;
        }
        // Only log the error if it is something other than a user manually closing the auth window.
        const errorMetaData: SentryErrorMetadata = {
          tags: {
            ownershipArea: 'trello-electric',
            feature: 'Planner',
          },
        };
        sendErrorEvent(error, errorMetaData);
        Analytics.taskFailed({
          taskName,
          source,
          traceId,
          error,
        });
      }
    },
    [
      analyticsSource,
      containers,
      intl,
      onSuccessfulAuth,
      setIsAuthenticating,
      email,
      isUseLoginHintEnabled,
      numberOfAccounts,
    ],
  );
};
